import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Code } from 'lucide-react';
import { UserStats } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { GroupSelector } from './GroupSelector';
import { AccountRole } from '../../types/accounts';
import { getNavigationItemsForRole } from '../../utils/navigationHelpers';
import { XP_PER_LEVEL, APP_NAME } from '../../config/constants';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  displayName: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  selectedGroup: GroupId;
  onSelectGroup: (group: GroupId) => void;
  activeAccountRole?: AccountRole;
}
export function Sidebar({
  currentView,
  onNavigate,
  stats,
  displayName,
  isCollapsed,
  onToggleCollapse,
  selectedGroup,
  onSelectGroup,
  activeAccountRole = 'learner'
}: SidebarProps) {
  // Get role-based navigation items
  const navItems = getNavigationItemsForRole(activeAccountRole);
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats.xp % XP_PER_LEVEL / XP_PER_LEVEL * 100;
  return <motion.aside className="hidden md:flex flex-col h-screen bg-white border-r border-slate-200/60 sticky top-0 z-30 shadow-[4px_0_24px_rgba(0,0,0,0.02)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-3709">
      {/* Logo Section - IMPROVED: More padding */}
      <div className="h-24 flex items-center px-7" data-id="element-3710">
        <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200 flex-shrink-0 transform rotate-3 hover:rotate-6 transition-transform" data-id="element-3711">
          <span className="text-white font-black text-xl" data-id="element-3712">M</span>
        </div>

        <AnimatePresence data-id="element-3713">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-xl tracking-tight whitespace-nowrap overflow-hidden" data-id="element-3714">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Group Selector - Only show for learners - IMPROVED: More margin */}
      {activeAccountRole === 'learner' && <div className="px-4 mb-3" data-id="element-3715">
          <GroupSelector selectedGroup={selectedGroup} onSelect={onSelectGroup} isCollapsed={isCollapsed} data-id="element-3716" />
        </div>}

      {/* Navigation Items - IMPROVED: Better spacing */}
      <nav className="flex-1 py-4 px-4 space-y-3 overflow-y-auto" data-id="element-3717">
        {navItems.map(item => {
        const isActive = currentView === item.id;
        return <button key={item.id} onClick={() => onNavigate(item.id)} className={`
                w-full flex items-center px-4 py-4 rounded-2xl transition-all duration-200 group relative overflow-hidden
                ${isActive ? 'bg-indigo-50 text-indigo-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                ${item.isDev ? 'mt-8 border-t border-slate-100 pt-6' : ''}
              `} title={isCollapsed ? item.label : undefined} data-id="element-3718">
              {/* Active Indicator Pill */}
              {isActive && <motion.div layoutId="activeIndicator" className="absolute inset-0 bg-indigo-50 rounded-2xl -z-10 border border-indigo-100" transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-3719" />}

              <item.icon className={`
                  w-6 h-6 flex-shrink-0 relative z-10 transition-colors
                  ${isActive ? 'text-indigo-600 fill-indigo-600/20' : 'text-slate-400 group-hover:text-slate-600'}
                `} strokeWidth={isActive ? 2.5 : 2} data-id="element-3720" />

              <AnimatePresence data-id="element-3721">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-bold' : 'font-semibold'}`} data-id="element-3722">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </button>;
      })}
      </nav>

      {/* User Profile Section - Only show for learners - IMPROVED: Better padding */}
      {activeAccountRole === 'learner' && <div className="p-4 border-t border-slate-100 bg-slate-50/50" data-id="element-3723">
          <div className={`
            flex items-center p-3 rounded-2xl bg-white border border-slate-200/60 shadow-sm
            ${isCollapsed ? 'justify-center' : 'justify-start'}
          `} data-id="element-3724">
            <div className="relative flex-shrink-0" data-id="element-3725">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-base border-2 border-white shadow-md" data-id="element-3726">
                {displayName.charAt(0)}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-amber-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-white shadow-sm flex items-center gap-0.5" data-id="element-3727">
                <Star className="w-2.5 h-2.5 fill-current" data-id="element-3728" />
                {stats.level}
              </div>
            </div>

            <AnimatePresence data-id="element-3729">
              {!isCollapsed && <motion.div initial={{
            opacity: 0,
            width: 0
          }} animate={{
            opacity: 1,
            width: 'auto'
          }} exit={{
            opacity: 0,
            width: 0
          }} className="ml-3 overflow-hidden flex-1 min-w-0" data-id="element-3730">
                  <div className="flex justify-between items-center mb-2" data-id="element-3731">
                    <div className="text-sm font-bold text-slate-800 truncate" data-id="element-3732">
                      {displayName}
                    </div>
                    <div className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full border border-indigo-100" data-id="element-3733">
                      {stats.xp} XP
                    </div>
                  </div>

                  {/* XP Progress Bar */}
                  <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden border border-slate-200/50" data-id="element-3734">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full transition-all duration-500" style={{
                width: `${progressToNextLevel}%`
              }} data-id="element-3735" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3 top-28 w-7 h-7 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-all hover:scale-110 z-40 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-3736">
        {isCollapsed ? <ChevronRight className="w-4 h-4" data-id="element-3737" /> : <ChevronLeft className="w-4 h-4" data-id="element-3738" />}
      </button>
    </motion.aside>;
}